import React, { useEffect, useState } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Typography } from 'antd';
import {
  acknowledgePermissionChangeAPI,
  checkPermissionChangeAPI,
} from '../../api/auth.api';

const { Text, Title } = Typography;

interface PermissionChangeNotificationType {
  userId: string;
  email: string;
  changeType: 'role_update' | 'permission_update' | 'access_revoked';
  timestamp: string;
  newPermissions?: string[];
  oldPermissions?: string[];
  changedBy?: string;
}

interface PermissionChangeNotificationProps {
  checkInterval?: number; // 체크 간격 (밀리초, 기본값: 30초)
}

function PermissionChangeNotification({
  checkInterval = 30000, // 기본값: 30초
}: PermissionChangeNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [notification, setNotification] =
    useState<PermissionChangeNotificationType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 권한 변경 확인
  const checkPermissionChange = async () => {
    try {
      const result = await checkPermissionChangeAPI();

      if (result.hasChange && result.notification) {
        setNotification(result.notification);
        setIsVisible(true);
        console.log('🔔 권한 변경 감지:', result.notification);
      }
    } catch (error) {
      console.error('권한 변경 확인 실패:', error);
      // 에러가 발생해도 사용자에게 알리지 않음 (백그라운드 체크이므로)
    }
  };

  // 권한 변경 알림 확인 완료
  const handleAcknowledge = async () => {
    setIsLoading(true);
    try {
      await acknowledgePermissionChangeAPI();
      setIsVisible(false);
      setNotification(null);

      // 페이지 새로고침
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('권한 변경 알림 확인 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 나중에 새로고침
  const handleLater = async () => {
    setIsVisible(false);
    // 알림을 확인하지 않으면 다음 체크 때 다시 표시됨
  };

  // 변경 타입에 따른 메시지
  const getChangeMessage = (changeType: string): string => {
    switch (changeType) {
      case 'role_update':
        return '역할이 변경되었습니다';
      case 'permission_update':
        return '권한이 변경되었습니다';
      case 'access_revoked':
        return '접근 권한이 해제되었습니다';
      default:
        return '권한이 변경되었습니다';
    }
  };

  // 주기적으로 권한 변경 확인
  useEffect(() => {
    // 초기 확인
    checkPermissionChange();

    // 주기적 확인 설정
    const interval = setInterval(checkPermissionChange, checkInterval);

    // 컴포넌트 언마운트 시 인터벌 제거
    return () => clearInterval(interval);
  }, [checkInterval]);

  // 페이지 포커스 시에도 확인
  useEffect(() => {
    const handleFocus = () => {
      checkPermissionChange();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  if (!notification) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span>권한 변경 알림</span>
        </div>
      }
      open={isVisible}
      onCancel={() => {}} // 모달 외부 클릭으로 닫기 방지
      closable={false} // X 버튼 제거
      footer={[
        <Button key='later' onClick={handleLater}>
          나중에
        </Button>,
        <Button
          key='reload'
          type='primary'
          loading={isLoading}
          onClick={handleAcknowledge}
        >
          지금 새로고침
        </Button>,
      ]}
      centered
      maskClosable={false}
    >
      <div style={{ padding: '16px 0' }}>
        <Title level={4} style={{ marginBottom: '16px', color: '#faad14' }}>
          {getChangeMessage(notification.changeType)}
        </Title>

        <div style={{ marginBottom: '16px' }}>
          <Text>
            관리자가 귀하의 권한을 업데이트했습니다.
            <br />
            변경사항을 적용하려면 페이지를 새로고침해야 합니다.
          </Text>
        </div>

        {notification.changedBy && (
          <div style={{ marginBottom: '12px' }}>
            <Text type='secondary'>변경자: {notification.changedBy}</Text>
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          <Text type='secondary'>
            변경 시간:{' '}
            {new Date(notification.timestamp).toLocaleString('ko-KR')}
          </Text>
        </div>

        {/* 개발 환경에서만 상세 정보 표시 */}
        {process.env.NODE_ENV === 'development' && (
          <details style={{ marginTop: '16px' }}>
            <summary style={{ cursor: 'pointer', color: '#666' }}>
              상세 정보 (개발용)
            </summary>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              <div>
                변경 전 권한: {notification.oldPermissions?.length || 0}개
              </div>
              <div>
                변경 후 권한: {notification.newPermissions?.length || 0}개
              </div>
              <div>변경 타입: {notification.changeType}</div>
            </div>
          </details>
        )}
      </div>
    </Modal>
  );
}

export default PermissionChangeNotification;
